import axios from "axios";
import { sharedRequestHeaders } from "./apiService";
import { installLoginTwoFactorReplay } from "./loginTwoFactorGate";
import { installUnauthorizedReplay } from "../auth/unauthorizedReplay";
import { installStaffConfirmation } from "./staffConfirmation/bindStaffConfirmation";
import type { StepUpHandling } from "./stepUp";
import {
  isHostAnswerableRefusal,
  noteIfConfirmationDemanded,
  isOperatorTwoFactorSetupRequired,
} from "./stepUp";
import { canHandOffToStepUpHost, challengeStepUp } from "./stepUpPromptBroker";
import { noteOperatorSetupRequired } from "./operatorTwoFactorSignal";

/**
 * Dedicated HTTP client for admin money operations that may require a 2FA
 * step-up (e.g. crypto withdrawal approve/reject).
 *
 * Why a separate instance instead of the shared `fetcher`/`useApiMutation` layer:
 *   1. That layer cannot forward custom headers, so it can't send `X-2FA-Code`.
 *   2. The global axios response interceptor (services/apiService.ts) hard-
 *      redirects the whole app to `/unauthorized` on ANY 403 — which is exactly
 *      how the API returns the `TWO_FACTOR_REQUIRED` challenge. Going through the
 *      interceptor would log the admin out instead of prompting for a code.
 *
 * This instance is created by `axios.create()`, so it does NOT inherit those
 * interceptors. It takes the bearer from the token provider (through
 * sharedRequestHeaders) and the base URL from `axios.defaults` at request time,
 * mirroring the `refundClient` pattern already used in dposAdminService.
 */
// Bounded timeout so a hung approve/reject never leaves the caller's promise
// (and the 2FA modal) stuck indefinitely; timeouts surface as an ApiError below.
const client = axios.create({ timeout: 20000 });
installStaffConfirmation(client);
installLoginTwoFactorReplay(client);
installUnauthorizedReplay(client);

// The error classes live in twoFactorErrors.ts (the prompt broker needs them
// without this module's axios instance); this stays their public home.
import {
  ApiError,
  TwoFactorCancelledError,
  isTwoFactorCancelled,
} from "./twoFactorErrors";

export { ApiError, TwoFactorCancelledError, isTwoFactorCancelled };

interface V2Envelope<TData> {
  success?: boolean;
  data?: TData;
  message?: string;
  error?: string;
  code?: string;
  /** Structured extras, e.g. a refused TOTP code's `reason` ("already_used"). */
  details?: unknown;
}

/**
 * Perform an admin money operation, optionally with a 2FA code. Unwraps the v2
 * `{ success, data }` envelope and normalises any failure into an `ApiError`
 * that preserves the server's `code` (e.g. `TWO_FACTOR_REQUIRED`,
 * `INVALID_TWO_FACTOR_CODE`).
 *
 * `extraHeaders` carries the headers a money op needs beyond the step-up — in
 * practice `Idempotency-Key`, which the DPOS refund sends alongside the same key
 * in the body to match the server's dual read (see dposAdminService). The 2FA
 * code is applied last so a caller can never overwrite it by accident.
 *
 * `options.stepUp: "host"` is for a caller with NO code field of its own
 * (ENG-1378): a "set up your authenticator first" or TWO_FACTOR_REQUIRED answer is
 * then handed to the OperatorTwoFactorPromptHost, which prompts once and re-sends
 * this same request with the operator's first code; the promise settles with that
 * answer. Every existing caller runs inside useStepUp / useTwoFactorAction and
 * handles those answers itself, so it passes nothing and sees nothing new.
 */
export interface MoneyOpRequestOptions {
  stepUp?: StepUpHandling;
  /** What the operator was doing, for the prompt's copy. */
  stepUpAction?: string;
  /**
   * Called with the whole success body before the envelope is unwrapped, for
   * what the api sends beside `data` (a rich-text save's `warnings`). Also
   * called for the answer to a step-up re-send.
   */
  onAnswer?: (body: unknown) => void;
}

export async function moneyOpRequest<TResponse = unknown>(
  method: string,
  url: string,
  data?: unknown,
  twoFactorCode?: string,
  extraHeaders?: Record<string, string>,
  options?: MoneyOpRequestOptions
): Promise<TResponse> {
  try {
    return await sendMoneyOp<TResponse>(
      method,
      url,
      data,
      twoFactorCode,
      extraHeaders,
      options?.onAnswer
    );
  } catch (error) {
    // A fact about the operator, whatever the caller does with it: every code
    // field on the page gives way to the set-up prompt (see operatorTwoFactorSignal).
    if (isOperatorTwoFactorSetupRequired(error)) noteOperatorSetupRequired();
    noteIfConfirmationDemanded(error);
    if (
      canHandOffToStepUpHost(options?.stepUp, url) &&
      isHostAnswerableRefusal(error)
    ) {
      // The re-send carries no `options`, so its own answer can never come back here.
      return challengeStepUp<TResponse>({
        kind: isOperatorTwoFactorSetupRequired(error)
          ? "setup-required"
          : "code-required",
        action: options?.stepUpAction,
        resend: (code) =>
          sendMoneyOp<TResponse>(
            method,
            url,
            data,
            code,
            extraHeaders,
            options?.onAnswer
          ),
      });
    }
    throw error;
  }
}

async function sendMoneyOp<TResponse>(
  method: string,
  url: string,
  data: unknown,
  twoFactorCode: string | undefined,
  extraHeaders: Record<string, string> | undefined,
  onAnswer?: (body: unknown) => void
): Promise<TResponse> {
  try {
    const response = await client.request<V2Envelope<TResponse>>({
      method,
      url,
      baseURL: axios.defaults.baseURL,
      data,
      headers: {
        ...(await sharedRequestHeaders()),
        ...extraHeaders,
        ...(twoFactorCode ? { "X-2FA-Code": twoFactorCode } : {}),
      },
    });

    const body = response.data;
    // A `{ success: false }` body can arrive with a 2xx status; treat it as an error.
    if (body && typeof body === "object" && body.success === false) {
      throw new ApiError(
        body.message || body.error || "Request failed",
        body.code,
        response.status
      );
    }
    onAnswer?.(body);
    if (
      body &&
      typeof body === "object" &&
      "success" in body &&
      body.success === true
    ) {
      return body.data as TResponse;
    }
    return body as unknown as TResponse;
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }
    if (axios.isAxiosError(error)) {
      if (error.response) {
        const body = error.response.data as V2Envelope<unknown> | undefined;
        const refusal = new ApiError(
          body?.message || body?.error || error.message || "Request failed",
          body?.code,
          error.response.status
        );
        // Kept for isStepUpCodeAlreadyUsed: a spent code is not a wrong one.
        if (typeof body?.details === "object" && body.details !== null) {
          refusal.details = body.details as Record<string, unknown>;
        }
        throw refusal;
      }
      // No response = timeout / network failure. Map it into the ApiError path so
      // callers recover cleanly (show an error, unstick the modal) instead of a
      // raw axios error leaving the promise dangling.
      const isTimeout =
        error.code === "ECONNABORTED" || error.code === "ETIMEDOUT";
      throw new ApiError(
        isTimeout
          ? "The request timed out. Verify the operation in the list before retrying."
          : "Network error. Please check your connection and try again.",
        isTimeout ? "REQUEST_TIMEOUT" : "NETWORK_ERROR"
      );
    }
    throw error instanceof Error
      ? error
      : new Error("An unknown error occurred");
  }
}
